import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiCalendar, FiClock, FiMapPin, FiUsers, FiPlus, FiEdit3, FiTrash2,
  FiDollarSign, FiTicket, FiTrendingUp, FiBarChart2, FiDownload, FiSearch,
  FiFilter, FiX, FiCheck, FiStar, FiShare2, FiMail, FiPhone, FiUser,
  FiGrid, FiList, FiPackage, FiTarget, FiAward, FiZap, FiAlertCircle,
  FiUserPlus, FiUserCheck, FiVideo, FiCamera, FiSend, FiShield, FiTrendingDown,
  FiCoffee, FiNavigation, FiAperture
} = FiIcons;

const TicketManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('games');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Provider Database with Referral Counts
  const providers = [
    {
      id: 1,
      name: 'Dr. Sarah Mitchell',
      email: 'smitchell@dentalcare.com',
      phone: '(314) 555-0101',
      specialty: 'Orthodontist',
      referralCount: 47,
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [],
      assignedGames: [1, 3],
      testimonialSubmitted: true,
      lastAttended: '2025-10-11'
    },
    {
      id: 2,
      name: 'Dr. Michael Chen',
      email: 'mchen@stldental.com',
      phone: '(314) 555-0102',
      specialty: 'General Dentist',
      referralCount: 38,
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [2, 5, 7],
      assignedGames: [],
      testimonialSubmitted: false,
      lastAttended: null
    },
    {
      id: 3,
      name: 'Dr. Emily Rodriguez',
      email: 'erodriguez@smilecenter.com',
      phone: '(314) 555-0103',
      specialty: 'Pediatric Dentist',
      referralCount: 52,
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [1, 7, 10],
      assignedGames: [7],
      testimonialSubmitted: true,
      lastAttended: '2025-11-15'
    },
    {
      id: 4,
      name: 'Dr. James Wilson',
      email: 'jwilson@oralhealth.com',
      phone: '(314) 555-0104',
      specialty: 'Oral Surgeon',
      referralCount: 31,
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [3, 8, 9],
      assignedGames: [],
      testimonialSubmitted: false,
      lastAttended: null
    },
    {
      id: 5,
      name: 'Dr. Lisa Thompson',
      email: 'lthompson@brightsmiles.com',
      phone: '(314) 555-0105',
      specialty: 'Cosmetic Dentist',
      referralCount: 44,
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [1, 2, 3, 7, 10],
      assignedGames: [1, 2],
      testimonialSubmitted: true,
      lastAttended: '2025-10-14'
    },
    {
      id: 6,
      name: 'Dr. Robert Martinez',
      email: 'rmartinez@dentalgroup.com',
      phone: '(314) 555-0106',
      specialty: 'Periodontist',
      referralCount: 29,
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop',
      status: 'active',
      preferences: [4, 6, 8],
      assignedGames: [],
      testimonialSubmitted: false,
      lastAttended: null
    }
  ];

  // 2025-26 STL Blues Home Games Schedule (VIP Only)
  const games = [
    {
      id: 1,
      opponent: 'Chicago Blackhawks',
      date: '2025-10-11',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'assigned',
      vipPackages: [
        { id: 1, seats: 2, section: 'Row A', location: 'Center Ice', assigned: true, provider: providers[0] },
        { id: 2, seats: 4, section: 'Row B', location: 'Center Ice', assigned: true, provider: providers[4] }
      ],
      image: 'https://images.unsplash.com/photo-1515703407324-5f753afd8be8?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'completed',
      applicants: 12
    },
    {
      id: 2,
      opponent: 'Minnesota Wild',
      date: '2025-10-14',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'assigned',
      vipPackages: [
        { id: 3, seats: 2, section: 'Row A', location: 'Penalty Box', assigned: true, provider: providers[4] },
        { id: 4, seats: 4, section: 'Row B', location: 'Center Ice', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1566577134770-3d85bb3a9cc4?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'pending',
      applicants: 8
    },
    {
      id: 3,
      opponent: 'Colorado Avalanche',
      date: '2025-10-18',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'lottery-open',
      vipPackages: [
        { id: 5, seats: 2, section: 'Row A', location: 'Center Ice', assigned: true, provider: providers[0] },
        { id: 6, seats: 4, section: 'Row A', location: 'Penalty Box', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1589487391730-58f20eb2c308?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'open',
      applicants: 15
    },
    {
      id: 4,
      opponent: 'Nashville Predators',
      date: '2025-10-25',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'lottery-open',
      vipPackages: [
        { id: 7, seats: 2, section: 'Row B', location: 'Center Ice', assigned: false, provider: null },
        { id: 8, seats: 4, section: 'Row A', location: 'Center Ice', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'open',
      applicants: 10
    },
    {
      id: 5,
      opponent: 'Dallas Stars',
      date: '2025-11-01',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'upcoming',
      vipPackages: [
        { id: 9, seats: 2, section: 'Row A', location: 'Penalty Box', assigned: false, provider: null },
        { id: 10, seats: 4, section: 'Row B', location: 'Center Ice', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'pending',
      applicants: 0
    },
    {
      id: 6,
      opponent: 'Winnipeg Jets',
      date: '2025-11-08',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'upcoming',
      vipPackages: [
        { id: 11, seats: 2, section: 'Row A', location: 'Center Ice', assigned: false, provider: null },
        { id: 12, seats: 4, section: 'Row B', location: 'Penalty Box', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1515703407324-5f753afd8be8?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'scheduled',
      applicants: 0
    },
    {
      id: 7,
      opponent: 'Toronto Maple Leafs',
      date: '2025-11-15',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'assigned',
      vipPackages: [
        { id: 13, seats: 2, section: 'Row A', location: 'Center Ice', assigned: true, provider: providers[2] },
        { id: 14, seats: 4, section: 'Row A', location: 'Center Ice', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1566577134770-3d85bb3a9cc4?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'completed',
      applicants: 18
    },
    {
      id: 8,
      opponent: 'Edmonton Oilers',
      date: '2025-11-22',
      time: '7:00 PM',
      venue: 'Enterprise Center',
      status: 'upcoming',
      vipPackages: [
        { id: 15, seats: 2, section: 'Row B', location: 'Center Ice', assigned: false, provider: null },
        { id: 16, seats: 4, section: 'Row A', location: 'Penalty Box', assigned: false, provider: null }
      ],
      image: 'https://images.unsplash.com/photo-1589487391730-58f20eb2c308?w=400&h=200&fit=crop',
      season: '2025-26',
      lotteryStatus: 'scheduled',
      applicants: 0
    }
  ];

  const stats = {
    totalGames: games.length,
    totalProviders: providers.length,
    totalReferrals: providers.reduce((sum, p) => sum + p.referralCount, 0),
    assignedPackages: games.reduce((sum, g) => sum + g.vipPackages.filter(p => p.assigned).length, 0),
    totalPackages: games.reduce((sum, g) => sum + g.vipPackages.length, 0),
    lotteryOpen: games.filter(g => g.lotteryStatus === 'open').length,
    testimonialsReceived: providers.filter(p => p.testimonialSubmitted).length
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'lottery-open': return 'bg-green-100 text-green-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'upcoming': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLotteryStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'pending': return 'bg-yellow-500';
      case 'scheduled': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredGames = games.filter(game => 
    game.opponent.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedProviders = [...providers].sort((a, b) => b.referralCount - a.referralCount);

  // Check if user is SuperAdmin
  if (user?.role !== 'SuperAdmin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50 flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl p-8 shadow-lg border border-red-200 max-w-md"
        >
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600" />
            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          </div>
          <p className="text-gray-600 mb-4">
            You don't have permission to access the STL Blues Ticket Management system. 
            This feature is restricted to SuperAdmin users only.
          </p>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Your Role:</strong> {user?.role || 'User'}
            </p>
            <p className="text-sm text-blue-600 mt-1">
              <strong>Required Role:</strong> SuperAdmin
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center space-x-4 mb-4 lg:mb-0">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 rounded-2xl flex items-center justify-center shadow-xl">
                <span className="text-white font-bold text-3xl">STL</span>
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">STL Blues VIP Tickets</h1>
                <p className="text-gray-600 mt-1">Provider Lottery & Assignment System • 2025-26 Season</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors flex items-center space-x-2 shadow-sm">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span className="font-medium">Export Report</span>
              </button>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span className="font-medium">Add Game</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-6 shadow-xl text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <SafeIcon icon={FiCalendar} className="w-7 h-7" />
              </div>
              <SafeIcon icon={FiTrendingUp} className="w-5 h-5 opacity-75" />
            </div>
            <p className="text-4xl font-bold mb-1">{stats.totalGames}</p>
            <p className="text-blue-100 text-sm font-medium">Total Home Games</p>
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs text-blue-100">
                <span className="font-semibold">{stats.lotteryOpen}</span> lottery open
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-6 shadow-xl text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <SafeIcon icon={FiUsers} className="w-7 h-7" />
              </div>
              <SafeIcon icon={FiTarget} className="w-5 h-5 opacity-75" />
            </div>
            <p className="text-4xl font-bold mb-1">{stats.totalProviders}</p>
            <p className="text-purple-100 text-sm font-medium">Active Providers</p>
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs text-purple-100">
                <span className="font-semibold">{stats.totalReferrals}</span> total referrals
              </p>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-6 shadow-xl text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <SafeIcon icon={FiTicket} className="w-7 h-7" />
              </div>
              <div className="text-right">
                <p className="text-xs text-green-100 font-medium">
                  {Math.round((stats.assignedPackages / stats.totalPackages) * 100)}%
                </p>
              </div>
            </div>
            <p className="text-4xl font-bold mb-1">{stats.assignedPackages}/{stats.totalPackages}</p>
            <p className="text-green-100 text-sm font-medium">VIP Packages Assigned</p>
            <div className="mt-3 pt-3 border-t border-white/20">
              <div className="w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-white h-2 rounded-full transition-all"
                  style={{ width: `${(stats.assignedPackages / stats.totalPackages) * 100}%` }}
                />
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-6 shadow-xl text-white"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <SafeIcon icon={FiVideo} className="w-7 h-7" />
              </div>
              <SafeIcon icon={FiCamera} className="w-5 h-5 opacity-75" />
            </div>
            <p className="text-4xl font-bold mb-1">{stats.testimonialsReceived}</p>
            <p className="text-orange-100 text-sm font-medium">Testimonials Received</p>
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-xs text-orange-100">
                <span className="font-semibold">{stats.totalProviders - stats.testimonialsReceived}</span> pending
              </p>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-2 shadow-sm border border-gray-100 mb-6 inline-flex"
        >
          {[
            { id: 'games', label: 'Games & Lottery', icon: FiCalendar },
            { id: 'providers', label: 'Provider Rankings', icon: FiAward },
            { id: 'assignments', label: 'Assignments', icon: FiCheck },
            { id: 'testimonials', label: 'Testimonials', icon: FiVideo }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-xl text-sm font-semibold transition-all flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </motion.div>

        {/* Games & Lottery Tab */}
        {activeTab === 'games' && (
          <div>
            {/* Search */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="relative flex-1 max-w-md">
                  <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search games by opponent..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="flex items-center space-x-2 bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                  >
                    <SafeIcon icon={FiGrid} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                  >
                    <SafeIcon icon={FiList} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Games Grid */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-4'}>
              {filteredGames.map((game, index) => (
                <motion.div
                  key={game.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all"
                >
                  <div className="relative">
                    <img src={game.image} alt={game.opponent} className="w-full h-56 object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute top-4 right-4 flex items-center space-x-2">
                      <span className={`px-3 py-1.5 text-xs font-bold rounded-full backdrop-blur-sm ${getStatusColor(game.status)}`}>
                        {game.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="absolute top-4 left-4">
                      <div className="flex items-center space-x-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
                        <div className={`w-2 h-2 rounded-full ${getLotteryStatusColor(game.lotteryStatus)}`} />
                        <span className="text-white text-xs font-semibold">
                          {game.lotteryStatus === 'open' ? 'Lottery Open' : 
                           game.lotteryStatus === 'completed' ? 'Lottery Complete' :
                           game.lotteryStatus === 'pending' ? 'Pending' : 'Scheduled'}
                        </span>
                      </div>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-bold text-white mb-1">vs {game.opponent}</h3>
                      <div className="flex items-center space-x-4 text-white/90 text-sm">
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                          <span>{new Date(game.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiClock} className="w-4 h-4" />
                          <span>{game.time}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* VIP Packages */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-bold text-gray-900 flex items-center space-x-2">
                          <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-500" />
                          <span>VIP Packages</span>
                        </h4>
                        <span className="text-xs text-gray-500">{game.applicants} applicants</span>
                      </div>
                      <div className="space-y-3">
                        {game.vipPackages.map((pkg) => (
                          <div
                            key={pkg.id}
                            className={`p-4 rounded-xl border-2 transition-all ${
                              pkg.assigned
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  pkg.assigned ? 'bg-green-500' : 'bg-gray-400'
                                }`}>
                                  <span className="text-white font-bold text-sm">{pkg.seats}</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm">
                                    {pkg.section} • {pkg.location}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {pkg.seats} seats • Glass level
                                  </p>
                                </div>
                              </div>
                              {pkg.assigned && (
                                <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            {pkg.assigned && pkg.provider && (
                              <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-green-200">
                                <img
                                  src={pkg.provider.avatar}
                                  alt={pkg.provider.name}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-gray-900 truncate">
                                    {pkg.provider.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {pkg.provider.referralCount} referrals
                                  </p>
                                </div>
                              </div>
                            )}
                            {!pkg.assigned && (
                              <button
                                onClick={() => {
                                  setSelectedGame(game);
                                  setShowProviderModal(true);
                                }}
                                className="w-full mt-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                Assign Provider
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Inclusions */}
                    <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                      <p className="text-xs font-bold text-gray-900 mb-2 flex items-center space-x-1">
                        <SafeIcon icon={FiPackage} className="w-3 h-3" />
                        <span>VIP Inclusions</span>
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {['Glass Seats', 'Food & Drinks', 'Parking Pass', 'VIP Access'].map((item, i) => (
                          <div key={i} className="flex items-center space-x-1.5">
                            <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-600" />
                            <span className="text-xs text-gray-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      {game.lotteryStatus === 'open' && (
                        <button
                          onClick={() => {
                            setSelectedGame(game);
                            setShowLotteryModal(true);
                          }}
                          className="flex-1 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center space-x-2 font-semibold text-sm"
                        >
                          <SafeIcon icon={FiTarget} className="w-4 h-4" />
                          <span>Run Lottery</span>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedGame(game);
                          setShowCreateModal(true);
                        }}
                        className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-semibold text-sm"
                      >
                        <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                        <span>Manage</span>
                      </button>
                      <button className="p-2.5 text-gray-400 hover:text-blue-600 transition-colors">
                        <SafeIcon icon={FiBarChart2} className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Provider Rankings Tab */}
        {activeTab === 'providers' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-1">Provider Rankings</h3>
                  <p className="text-purple-100 text-sm">Sorted by referral count (highest priority)</p>
                </div>
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
                  <SafeIcon icon={FiAward} className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {sortedProviders.map((provider, index) => (
                  <motion.div
                    key={provider.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-5 rounded-xl border-2 border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                    onClick={() => {
                      setSelectedProvider(provider);
                      setShowProviderModal(true);
                    }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <div className={`absolute -top-2 -left-2 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                          index === 0 ? 'bg-yellow-500' :
                          index === 1 ? 'bg-gray-400' :
                          index === 2 ? 'bg-orange-600' :
                          'bg-blue-500'
                        }`}>
                          {index + 1}
                        </div>
                        <img
                          src={provider.avatar}
                          alt={provider.name}
                          className="w-16 h-16 rounded-xl object-cover border-2 border-white shadow-md"
                        />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-bold text-gray-900 text-lg">{provider.name}</h4>
                          {provider.testimonialSubmitted && (
                            <SafeIcon icon={FiCamera} className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{provider.specialty}</p>
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiMail} className="w-3 h-3" />
                            <span>{provider.email}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiPhone} className="w-3 h-3" />
                            <span>{provider.phone}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
                          <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-gray-900">{provider.referralCount}</p>
                          <p className="text-xs text-gray-600">Referrals</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          {provider.assignedGames.length} games
                        </span>
                        {provider.preferences.length > 0 && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
                            {provider.preferences.length} prefs
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100"
          >
            <div className="p-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-1">Current Assignments</h3>
                  <p className="text-blue-100 text-sm">View all provider-game assignments</p>
                </div>
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
                  <SafeIcon icon={FiCheck} className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {games.filter(g => g.vipPackages.some(p => p.assigned)).map((game, index) => (
                  <motion.div
                    key={game.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="border-2 border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-all"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-xl font-bold text-gray-900">vs {game.opponent}</h4>
                        <p className="text-sm text-gray-600">
                          {new Date(game.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} • {game.time}
                        </p>
                      </div>
                      <span className={`px-3 py-1.5 text-xs font-bold rounded-full ${getStatusColor(game.status)}`}>
                        {game.status.replace('-', ' ').toUpperCase()}
                      </span>
                    </div>

                    <div className="space-y-3">
                      {game.vipPackages.filter(p => p.assigned).map((pkg) => (
                        <div
                          key={pkg.id}
                          className="flex items-center justify-between p-4 bg-green-50 border-2 border-green-200 rounded-xl"
                        >
                          <div className="flex items-center space-x-4">
                            <img
                              src={pkg.provider.avatar}
                              alt={pkg.provider.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div>
                              <p className="font-bold text-gray-900">{pkg.provider.name}</p>
                              <p className="text-sm text-gray-600">{pkg.provider.specialty}</p>
                              <div className="flex items-center space-x-3 mt-1">
                                <span className="text-xs text-gray-500">
                                  {pkg.seats} seats • {pkg.section} • {pkg.location}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <p className="text-sm font-semibold text-gray-900">{pkg.provider.referralCount} referrals</p>
                              {pkg.provider.testimonialSubmitted ? (
                                <span className="text-xs text-green-600 flex items-center space-x-1">
                                  <SafeIcon icon={FiCheck} className="w-3 h-3" />
                                  <span>Testimonial ✓</span>
                                </span>
                              ) : (
                                <span className="text-xs text-orange-600 flex items-center space-x-1">
                                  <SafeIcon icon={FiAlertCircle} className="w-3 h-3" />
                                  <span>Pending</span>
                                </span>
                              )}
                            </div>
                            <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                              <SafeIcon icon={FiMail} className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100"
          >
            <div className="p-6 bg-gradient-to-r from-orange-600 to-red-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-1">Marketing Testimonials</h3>
                  <p className="text-orange-100 text-sm">Video testimonials & game selfies from providers</p>
                </div>
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-xl">
                  <SafeIcon icon={FiVideo} className="w-8 h-8" />
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {providers.map((provider, index) => (
                  <motion.div
                    key={provider.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-6 rounded-xl border-2 ${
                      provider.testimonialSubmitted
                        ? 'border-green-200 bg-green-50'
                        : 'border-orange-200 bg-orange-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <img
                          src={provider.avatar}
                          alt={provider.name}
                          className="w-14 h-14 rounded-xl object-cover"
                        />
                        <div>
                          <h4 className="font-bold text-gray-900">{provider.name}</h4>
                          <p className="text-sm text-gray-600">{provider.specialty}</p>
                        </div>
                      </div>
                      {provider.testimonialSubmitted ? (
                        <div className="p-2 bg-green-500 rounded-lg">
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
                        </div>
                      ) : (
                        <div className="p-2 bg-orange-500 rounded-lg">
                          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {provider.testimonialSubmitted ? (
                      <div>
                        <div className="mb-3">
                          <div className="aspect-video bg-gray-900 rounded-lg flex items-center justify-center mb-2">
                            <SafeIcon icon={FiVideo} className="w-12 h-12 text-white" />
                          </div>
                          <p className="text-sm text-gray-700 font-medium">
                            Testimonial submitted on {provider.lastAttended}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button className="flex-1 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                            View Content
                          </button>
                          <button className="p-2 text-gray-400 hover:text-green-600 transition-colors">
                            <SafeIcon icon={FiDownload} className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-3 p-4 bg-white rounded-lg border-2 border-dashed border-orange-300">
                          <div className="flex items-center space-x-2 text-orange-700 mb-2">
                            <SafeIcon icon={FiSend} className="w-4 h-4" />
                            <p className="text-sm font-semibold">Testimonial Pending</p>
                          </div>
                          <p className="text-xs text-gray-600">
                            Automated reminder will be sent after game attendance
                          </p>
                        </div>
                        <button className="w-full py-2 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2">
                          <SafeIcon icon={FiMail} className="w-4 h-4" />
                          <span>Send Reminder</span>
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Provider Detail Modal */}
        <AnimatePresence>
          {showProviderModal && selectedProvider && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowProviderModal(false);
                setSelectedProvider(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center space-x-4">
                      <img
                        src={selectedProvider.avatar}
                        alt={selectedProvider.name}
                        className="w-20 h-20 rounded-xl object-cover border-4 border-white shadow-lg"
                      />
                      <div>
                        <h3 className="text-2xl font-bold mb-1">{selectedProvider.name}</h3>
                        <p className="text-purple-100 text-sm mb-2">{selectedProvider.specialty}</p>
                        <div className="flex items-center space-x-3">
                          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                            Rank #{sortedProviders.findIndex(p => p.id === selectedProvider.id) + 1}
                          </span>
                          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold">
                            {selectedProvider.referralCount} Referrals
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowProviderModal(false);
                        setSelectedProvider(null);
                      }}
                      className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                      <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600" />
                      <span>Contact Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2 text-gray-700">
                        <SafeIcon icon={FiMail} className="w-4 h-4" />
                        <span>{selectedProvider.email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-gray-700">
                        <SafeIcon icon={FiPhone} className="w-4 h-4" />
                        <span>{selectedProvider.phone}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                      <SafeIcon icon={FiTarget} className="w-5 h-5 text-purple-600" />
                      <span>Game Preferences</span>
                    </h4>
                    {selectedProvider.preferences.length > 0 ? (
                      <div className="space-y-2">
                        {selectedProvider.preferences.map((gameId, index) => {
                          const game = games.find(g => g.id === gameId);
                          return game ? (
                            <div key={gameId} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                              <div>
                                <p className="font-semibold text-gray-900">#{index + 1} Choice: vs {game.opponent}</p>
                                <p className="text-xs text-gray-600">
                                  {new Date(game.date).toLocaleDateString()} • {game.time}
                                </p>
                              </div>
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No preferences submitted</p>
                    )}
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                      <SafeIcon icon={FiTicket} className="w-5 h-5 text-green-600" />
                      <span>Assigned Games</span>
                    </h4>
                    {selectedProvider.assignedGames.length > 0 ? (
                      <div className="space-y-2">
                        {selectedProvider.assignedGames.map((gameId) => {
                          const game = games.find(g => g.id === gameId);
                          return game ? (
                            <div key={gameId} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <div>
                                <p className="font-semibold text-gray-900">vs {game.opponent}</p>
                                <p className="text-xs text-gray-600">
                                  {new Date(game.date).toLocaleDateString()} • {game.time}
                                </p>
                              </div>
                              <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600" />
                            </div>
                          ) : null;
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No games assigned yet</p>
                    )}
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center space-x-2">
                      <SafeIcon icon={FiVideo} className="w-5 h-5 text-orange-600" />
                      <span>Testimonial Status</span>
                    </h4>
                    {selectedProvider.testimonialSubmitted ? (
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-green-700 mb-2">
                          <SafeIcon icon={FiCheck} className="w-5 h-5" />
                          <p className="font-semibold">Testimonial Submitted</p>
                        </div>
                        <p className="text-sm text-gray-600">Last attended: {selectedProvider.lastAttended}</p>
                      </div>
                    ) : (
                      <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-orange-700 mb-2">
                          <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
                          <p className="font-semibold">Testimonial Pending</p>
                        </div>
                        <p className="text-sm text-gray-600">Automated reminder will be sent after game</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowProviderModal(false);
                      setSelectedProvider(null);
                    }}
                    className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button className="py-2.5 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all">
                    Send Email
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Lottery Modal */}
        <AnimatePresence>
          {showLotteryModal && selectedGame && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowLotteryModal(false);
                setSelectedGame(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-bold mb-1">Run Lottery Selection</h3>
                      <p className="text-green-100 text-sm">vs {selectedGame.opponent} • {new Date(selectedGame.date).toLocaleDateString()}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowLotteryModal(false);
                        setSelectedGame(null);
                      }}
                      className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div className="p-5 bg-blue-50 border-2 border-blue-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-blue-900 mb-1">Lottery Algorithm</h4>
                        <p className="text-sm text-blue-800 mb-2">
                          Providers are ranked by referral count. Highest-ranked providers get first selection from their top preferences.
                        </p>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• Providers submit 3-5 game preferences</li>
                          <li>• Ranked by total referrals (highest = priority)</li>
                          <li>• Automatic assignment based on availability</li>
                          <li>• Automated email notifications sent</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Applicants ({selectedGame.applicants})</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {sortedProviders
                        .filter(p => p.preferences.includes(selectedGame.id))
                        .map((provider, index) => (
                          <div
                            key={provider.id}
                            className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-blue-300 transition-all"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="relative">
                                <div className={`absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                  index === 0 ? 'bg-yellow-500' :
                                  index === 1 ? 'bg-gray-400' :
                                  index === 2 ? 'bg-orange-600' :
                                  'bg-blue-500'
                                }`}>
                                  {index + 1}
                                </div>
                                <img
                                  src={provider.avatar}
                                  alt={provider.name}
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{provider.name}</p>
                                <p className="text-xs text-gray-600">{provider.specialty}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-blue-600">{provider.referralCount}</p>
                              <p className="text-xs text-gray-600">referrals</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>

                  <div className="p-5 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
                    <h4 className="font-bold text-gray-900 mb-2 flex items-center space-x-2">
                      <SafeIcon icon={FiZap} className="w-5 h-5 text-purple-600" />
                      <span>Automation Actions</span>
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1.5">
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span>Send winner notification emails with ticket details</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span>Include parking pass, food vouchers, VIP access info</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span>Request video testimonial or game selfie (marketing)</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span>Send game day reminder 24 hours before</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span>Post-game follow-up for testimonial submission</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowLotteryModal(false);
                      setSelectedGame(null);
                    }}
                    className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button className="py-2.5 px-6 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all flex items-center space-x-2">
                    <SafeIcon icon={FiTarget} className="w-5 h-5" />
                    <span>Run Lottery & Assign</span>
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TicketManagement;